import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from "react-message-source";
import { Grid, Header, Icon, Table } from "semantic-ui-react";
import { Field } from "react-final-form";
import CheckBox from "components/final-form/CheckBox";
import TextArea from "components/final-form/TextArea";
import Input from "components/final-form/Input";
import { DiagnosisDto, PatientDiagnosisDto } from "ts-types/api.types";
import styled from "styled-components";
import ToolbarButton from "components/layout/ToolbarButton";
import ConfirmActionPopup from "components/ConfirmActionPopup";
import PraxFormGrid from 'components/layout/PraxFormGrid';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import { PxDropdownSc } from 'components/final-form/Select';

const ToggleColor = styled(Grid.Column)`
   &.ui.toggle.checkbox input:checked~.box:before, .ui.toggle.checkbox input:checked~label:before {
    background-color: rgb(255,17,51) !important;
}
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-bottom: 5px;
  margin-bottom: 0px;
  margin-top: 1rem;
`;

const HeaderTitle = styled.div`
  width: 85%;
  margin-bottom: 0;
`;

const DiagnosisContainer = styled.div``;

const DiagnosisSelectionContainer = styled.span`
  display: inline-block;
  width: 85%;
`;

const AddDiagnosisContainer = styled.span`
  width: 15%;
  display: inline-block;
  height: 2.6rem;
  vertical-align: top;
`;

const ScrollableTableContainer = styled.div`
  max-height: 241px;
  overflow-y: auto;
  width: 100%;
  border: 1px solid rgba(34,36,38,.15);
  box-shadow: none;
  border-radius: .28571429rem;
  margin-bottom: 1rem;

  & .ui.table {
    border: none;
  }

   & .ui.table thead tr:first-child > th {
      position: sticky !important;
      top: 0;
      z-index: 2;
      background: #fff;
  }

  & .ui.table tbody tr:last-child > td {
      border-bottom: 1px solid rgba(34,36,38,.15)
  }
`;

const InvoiceProblematic = styled(Grid.Column)`
  padding-left: 0.85rem;
  color: #db2828;
`;

const InvoiceProblematicIcon = styled.span`
  padding-right: 0.275rem;
`;

const InvoiceProblematicText = styled.span``;

const toolbarBtnStyle = {
  paddingTop: "0.4rem",
  paddingBottom: "0.7rem"
};

interface Props extends MessageSourceProps {
  diagnoses: Array<DiagnosisDto>,
  patientDiagnoses: Array<PatientDiagnosisDto>,
  onAddDiagnosis: (diagnosis: DiagnosisDto) => void,
  onDeleteDiagnosis: (patientDiagnosisIndex: number) => void,
  invoiceProblematic: boolean
}

interface State {
  selectedDiagnosis?: DiagnosisDto,
  diagnoses: Array<DropdownOption>
}

class RemarksPane extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      selectedDiagnosis: undefined,
      diagnoses: []
    };
  }

  componentDidUpdate({diagnoses: prevDiagnoses}: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {

    const {diagnoses} = this.props;

    if (diagnoses && prevDiagnoses && diagnoses.length != prevDiagnoses.length) {
      this.setState({
        diagnoses: mapToDropdownOptionArray(diagnoses, "description")
      })
    }
  }

  onDiagnosisChange = (diagnosisId: any) => {

    const {diagnoses} = this.props;

    this.setState({
      selectedDiagnosis: diagnoses.find(diagnosis => diagnosis.id == diagnosisId)
    })
  };

  addDiagnosis = () => {

    const {onAddDiagnosis} = this.props;
    const {selectedDiagnosis} = this.state;

    if (selectedDiagnosis) {
      onAddDiagnosis(selectedDiagnosis);
    }
  };

  deleteDiagnosis = (index: number) => {

    const {onDeleteDiagnosis} = this.props;

    onDeleteDiagnosis(index);
  };

  render() {
    return (
        <FormContainer>
          <FormContent>
            <PraxFormGrid stackable>
              {this.renderProblematicSegment()}
              {this.renderRemarksSegment()}
              {this.renderPermanentDiagnosisSegment()}
              {this.renderIdentificationSegment()}
            </PraxFormGrid>
          </FormContent>
        </FormContainer>
    );
  }

  renderProblematicSegment = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <Grid.Row>
            <ToggleColor width={3}>
              <Field
                  name={"doctorProblematic"}
                  component={CheckBox}
                  label={getMessage("patient.problematic")}
                  toggle={true}
              />
            </ToggleColor>
            <Grid.Column width={13} verticalAlign="middle">
              {this.renderProblematicHeader()}
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    )
  };

  renderRemarksSegment = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column>
              <Header>{getMessage("remarks")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Field
                  name={"remark"}
                  component={TextArea}
                  label={getMessage("remarks")}
                  style={{maxHeight: "160px", minHeight: "115px"}}
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    )
  };

  renderPermanentDiagnosisSegment = (): React.ReactNode => {

    const {getMessage, patientDiagnoses} = this.props;
    const {diagnoses, selectedDiagnosis} = this.state;

    const mappedPatientDiagnosis = patientDiagnoses ? patientDiagnoses.map((patientDiagnosis, index) => {
      return (
          <Table.Row key={index}>
            <Table.Cell>{patientDiagnosis.code}</Table.Cell>
            <Table.Cell>{patientDiagnosis.description}</Table.Cell>
            <Table.Cell>
              <ConfirmActionPopup
                  message={getMessage("diagnosis.confirmDelete")}
                  renderTarget={this.renderDeleteTrigger()}
                  onConfirmAction={() => this.deleteDiagnosis(index)}
              />
            </Table.Cell>
          </Table.Row>
      )
    }) : [];

    return (
        <React.Fragment>
          <HeaderContainer>
            <HeaderTitle>
              <Header>
                {getMessage("patient.permanentDiagnosis")}
              </Header>
            </HeaderTitle>
          </HeaderContainer>
          <Grid.Row>
            <Grid.Column>
              <ScrollableTableContainer>
                <Table celled padded size="small">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell width={2}>{getMessage("code")}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage("description")}</Table.HeaderCell>
                      <Table.HeaderCell width={2}>{getMessage("actions")}</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {mappedPatientDiagnosis}
                  </Table.Body>
                </Table>
              </ScrollableTableContainer>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <DiagnosisContainer>
                <DiagnosisSelectionContainer>
                  <PxDropdownSc
                      selection
                      onChange={(param: any, data: any) => this.onDiagnosisChange(data.value)}
                      search
                      options={diagnoses}
                      fluid
                  />
                </DiagnosisSelectionContainer>
                <AddDiagnosisContainer>
                  <ToolbarButton iconName="plus"
                                 iconSize="large"
                                 disabled={!selectedDiagnosis}
                                 onClick={this.addDiagnosis}
                                 toolbarButtonStyle={toolbarBtnStyle}
                                 dataTooltip={getMessage("patient.permanentDiagnosis.add")}
                  />
                </AddDiagnosisContainer>
              </DiagnosisContainer>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    )
  };

  renderIdentificationSegment = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column>
              <Header>{getMessage("patient.identification")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Field
                  name={"identification"}
                  component={Input}
                  maxLength={5}
                  placeholder={getMessage("patient.identification")}
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    )
  };

  renderProblematicHeader = (): React.ReactNode => {

    const {getMessage, invoiceProblematic} = this.props;

    if (invoiceProblematic) {
      return (
          <React.Fragment>
            <Grid.Row>
              <InvoiceProblematic>
                <InvoiceProblematicIcon>
                  <Icon name="exclamation triangle" aria-hidden="true"/>
                </InvoiceProblematicIcon>
                <InvoiceProblematicText>
                  {getMessage("patient.invoiceProblematic")}
                </InvoiceProblematicText>
              </InvoiceProblematic>
            </Grid.Row>
          </React.Fragment>
      )
    }

    return (
        <React.Fragment>
        </React.Fragment>
    )
  };

  renderDeleteTrigger = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <ToolbarButton iconName="trash"
                       dataTooltip={getMessage("patient.permanentDiagnosis.delete")}
        />
    );
  }
}

export default withMessages(RemarksPane);
